import type { When } from '@shared/common/when';
import { createWhen } from '@shared/common/when';
import type { AppSelectOption } from '@shared/form-fields/options/select-option';
import type { DiaryEvent } from '@shared/models/diary-event';
import { isBefore, isBeforeNow, isSameDay, isToday, isTomorrow, isYesterday, } from '@shared/utils/date-time';
import { DateTime, Interval } from 'luxon';
import { Presenter } from './presenter';

export class DiaryEventPresenter<T extends DiaryEvent> extends Presenter<T> {
    get times() {
        return this._get('times', () => `${this.startTime} - ${this.finishTime}`);
    }

    get when() {
        return this._get('when', () => this.formatDateTimeRange(this.start, this.finish));
    }

    get whenObject(): When {
        return this._get('whenObject', () => createWhen(
            this.toDateTime('start'),
            this.data.duration
        ));
    }

    get isPastScheduledFinish() {
        return isBefore(this.finish, new Date());
    }

    get spansMultipleDays() {
        return this._get('spansMultipleDays', () => !isSameDay(this.start.toJSDate(), this.finish.toJSDate()));
    }

    get dates() {
        return this._get('dates', () => {
            const start = this.start.startOf('day');
            const finish = this.finish.startOf('day');
            const dates = [];

            let date = start;

            while (date <= finish) {
                dates.push(date);
                date = date.plus({ day: 1 });
            }

            return dates;
        });
    }

    get datesOptions(): AppSelectOption<DateTime>[] {
        return this._get('datesOptions', () => this.dates.map(d => ({ label: this.formatDateShort(d), value: d })));
    }

    get duration(): string {
        return this.durationReadable;
    }

    get durationSeconds(): number {
        return this.data.duration;
    }

    get durationReadable(): string {
        return this._get('durationReadable', () => this.formatDuration(this.data.duration));
    }

    get durationShort() {
        return this._get('durationShort', () => this.formatDurationShort(this.data.duration));
    }

    get startTimestamp() {
        return this._get('start', () => this.formatTimestamp(this.start));
    }

    get start(): DateTime {
        return this.toDateTime('start');
    }

    get startReadable() {
        return this._get('startReadable', () => this.formatTimestamp(this.toDateTime('start')));
    }

    get startDate(): string {
        return this._get('startDate', () => this.formatDate(this.toDateTime('start')));
    }

    get dateRange(): string {
        return this._get('dateRange', () => this.formatDateRange(this.start, this.finish));
    }

    get startDay() {
        return this._get('startDay', () => this.start.toFormat('ddd'));
    }

    get startDateRelative() {
        return this._get('startDateRelative', () => {
            if (isYesterday(this.start.toJSDate())) {
                return 'Yesterday';
            }

            if (isToday(this.start.toJSDate())) {
                return 'Today';
            }

            if (isTomorrow(this.start.toJSDate())) {
                return 'Tomorrow';
            }
        });
    }

    get startDateShort() {
        return this._get('startDateShort', () => this.formatDateShort(this.start));
    }

    get startTime() {
        return this._get('startTime', () => this.formatTime(this.start));
    }

    get finish(): DateTime {
        return this._get('finish', () => this.start?.plus({ seconds: this.data.duration }));
    }

    get finishTimestamp() {
        // Its own cache key: sharing 'finish' with the getter above would hand `finish` a string.
        return this._get('finishTimestamp', () => this.formatTime(this.finish));
    }

    get finishReadable() {
        return this._get('finishReadable', () => this.formatDateTimeReadable(this.finish));
    }

    get finishDate() {
        return this._get('finishDate', () => this.formatDate(this.finish));
    }

    get finishTime() {
        return this._get('finishTime', () => this.formatTime(this.finish));
    }

    get isCurrentlyHappening() {
        return Interval.fromDateTimes(this.start, this.finish).contains(DateTime.local());
    }

    get hasStarted() {
        return isBeforeNow(this.start);
    }

    get hasFinished() {
        return isBeforeNow(this.finish);
    }
}
